import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ColumnIn, GapDetail, GapRow, RegistryColumn, RegistryTable, lhRegistryApiRef } from './api';
import { ColumnHistoryPanel } from './ColumnHistoryPanel';
import { Pill, Seg, Tone, fmtAt } from './ui';

export const STATE_LABEL: Record<string, string> = {
  ok: '일치', gap: 'GAP', partial: '일부 판정', unknown: '판정 불가', no_columns: '컬럼 미등록', excluded: '제외', unmapped: '매핑 없음',
  skipped: '건너뜀', query: '쿼리 소스', table_missing: '대상 테이블 없음',
};
const MISSING_HINT = '수집 SQL에서 이름이 바뀌었을 수 있음 — 정밀화 DPD-13930';
/** 컬럼 판정(lh_column_gap.verdict) → 칩. 제외·삭제·스냅샷 전·길이 차이는 GAP 으로 세지 않는다 */
const VERDICT: Record<string, [string, Tone]> = {
  OK: ['일치', 'ok'], TYPE: ['타입', 'warn'], 'TYPE?': ['타입 미등록', 'warn'], LEN: ['길이 차이', 'neutral'], MISSING: ['누락', 'error'], EXTRA: ['초과', 'error'],
  EXCLUDED: ['제외', 'neutral'], DROPPED_EXISTS: ['삭제 등록', 'neutral'], N_COLLECTED: ['미수집', 'neutral'], PENDING: ['스냅샷 전', 'info'],
};
const SIDES = ['source', 'lake'] as const;
type Side = (typeof SIDES)[number];

const VerdictPill = ({ row, side, fallback }: { row?: GapRow; side: Side; fallback: [string, Tone] }) => {
  if (!row) return <Pill tone={fallback[1]}>{fallback[0]}</Pill>;
  const [text, tone] = VERDICT[row.verdict] ?? [row.verdict, 'neutral' as Tone];
  if (row.verdict === 'TYPE') return <Pill tone={tone}>타입 → {row.snapshotType}</Pill>;
  // 수집 쪽 EXTRA 는 GAP 이 아니다 (is_gap = EXTRA AND side = 'source')
  if (row.verdict === 'EXTRA' && side === 'lake') return <Pill tone="neutral">{text}</Pill>;
  return <Pill tone={tone} title={row.verdict === 'MISSING' ? MISSING_HINT : undefined}>{text}</Pill>;
};

type Props = {
  table: RegistryTable;
  readOnly: boolean;
  /** 저장 대기 중인 컬럼 변경 — 상세의 저장 버튼이 티켓·사유와 함께 보낸다 */
  drop: string[];
  add: Record<string, string | null>;
  onToggleDrop: (name: string) => void;
  onToggleAdd: (name: string, dataType: string | null) => void;
  /** 기존 컬럼의 타입·collect_yn 수정분 (원래 값과 다른 컬럼만) */
  update: Record<string, ColumnIn>;
  /** 원래 값으로 돌아오면 undefined */
  onEdit: (name: string, u: ColumnIn | undefined) => void;
};

/** 테이블의 컬럼 표 + 스냅샷 대비 GAP. 컬럼 이름을 누르면 그 컬럼의 이력이 열린다 */
export const RegistryColumns = ({ table, readOnly, drop, add, onToggleDrop, onToggleAdd, update, onEdit }: Props) => {
  const api = useApi(lhRegistryApiRef);
  const [cols, setCols] = useState<RegistryColumn[]>();
  const [gap, setGap] = useState<GapDetail>();
  const [error, setError] = useState<string>();
  const [showDropped, setShowDropped] = useState(false);
  const [open, setOpen] = useState<string>();
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.listColumns(table.id).then(c => !cancelled && setCols(c)).catch(e => !cancelled && setError(e.message));
    api.gap(table.id).then(g => !cancelled && setGap(g)).catch(() => undefined); // GAP 은 보조 정보 — 실패해도 컬럼은 보인다
    return () => {
      cancelled = true;
    };
  }, [api, table.id, table.version]);

  const body = () => {
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!cols) return <CircularProgress size={20} />;
    const bySide = Object.fromEntries(SIDES.map(s => [s, new Map((gap?.[s]?.rows ?? []).map(r => [r.columnName.toLowerCase(), r]))])) as Record<Side, Map<string, GapRow>>;
    const known = new Set(cols.map(c => c.columnName.toLowerCase()));
    // 레지스트리에 없는 스냅샷 컬럼(EXTRA) — 원천·수집 어느 쪽에서 왔는지 모아 "추가" 대상으로 보여 준다
    const extras = new Map<string, { name: string; type: string | null; sides: Side[] }>();
    for (const s of SIDES)
      for (const r of gap?.[s]?.rows ?? [])
        if (r.verdict === 'EXTRA' && !known.has(r.columnName.toLowerCase())) {
          const e = extras.get(r.columnName.toLowerCase()) ?? { name: r.columnName, type: r.snapshotType, sides: [] };
          e.sides.push(s);
          extras.set(r.columnName.toLowerCase(), e);
        }
    const excluded = table.collectYn === 'N';
    const shown = cols.filter(c => showDropped || c.status === 'active');
    const snapAt = (s: Side) => {
      const at = gap?.[s]?.capturedAt ?? gap?.[s]?.sourceAsOf;
      return at ? fmtAt(at) : '—';
    };

    const empty = !cols.length && !extras.size;
    const emptyText = <Typography variant="body2" color="textSecondary">컬럼 미등록 — 컬럼 정보가 없어 GAP 을 판정하지 않습니다.</Typography>;
    if (empty && readOnly) return emptyText;
    // 수정 중인 값 — 수정분이 없으면 원래 값
    const typeOf = (c: RegistryColumn) => {
      const u = update[c.columnName];
      return u && 'dataType' in u ? u.dataType ?? '' : c.dataType ?? '';
    };
    const ynOf = (c: RegistryColumn) => update[c.columnName]?.collectYn ?? c.collectYn;
    const edit = (c: RegistryColumn, p: { dataType?: string; collectYn?: 'Y' | 'N' }) => {
      const dataType = p.dataType ?? typeOf(c);
      const yn = p.collectYn ?? ynOf(c);
      const u: ColumnIn = { columnName: c.columnName };
      if (dataType !== (c.dataType ?? '')) u.dataType = dataType || null;
      if (yn !== c.collectYn) u.collectYn = yn;
      onEdit(c.columnName, Object.keys(u).length > 1 ? u : undefined);
    };
    // 자유 입력 추가: 스냅샷에 없는 컬럼도 이름·타입을 직접 넣어 추가한다
    const taken = new Set([...known, ...Object.keys(add).map(n => n.toLowerCase())]);
    let newNameError = '';
    if (/\s/.test(newName)) newNameError = '공백 없는 이름';
    else if (taken.has(newName.toLowerCase())) newNameError = '이미 있는 컬럼';
    const freeAdds = Object.keys(add).filter(n => !extras.has(n.toLowerCase()));
    return (
      <>
        {empty && emptyText}
        {excluded ? (
          <Typography variant="body2" color="textSecondary">수집 제외(collect_yn=N) 테이블 — GAP 을 판정하지 않습니다.</Typography>
        ) : (
          <Typography variant="caption" color="textSecondary">원천 스냅샷 {snapAt('source')} · 수집 스냅샷 {snapAt('lake')}<br />제외·삭제 컬럼은 GAP 이 아닙니다.</Typography>
        )}
        <Box style={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['column', 'type', 'yn', '상태', '추가일', '삭제일', '원천', '수집'].map(h => <TableCell key={h} style={{ whiteSpace: 'nowrap' }}>{h}</TableCell>)}
                <TableCell><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>변경</span></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(c => {
                const pend = drop.includes(c.columnName);
                const editable = !readOnly && !pend && c.status === 'active';
                const edited = !!update[c.columnName];
                // 판정 행이 없을 때: 삭제 컬럼은 삭제 등록, 수집 안 하는 컬럼·테이블은 제외, 그 밖은 스냅샷 없음
                let fallback: [string, Tone] = ['—', 'neutral'];
                if (c.status === 'dropped') fallback = ['삭제 등록', 'neutral'];
                else if (c.collectYn === 'N' || excluded) fallback = ['제외', 'neutral'];
                let state = `추가 v${c.addedVersion}`;
                if (edited) state = '수정 예정';
                if (pend) state = '삭제 예정';
                if (c.status === 'dropped') state = `삭제 v${c.droppedVersion}`;
                return (
                  <TableRow key={c.id} selected={edited && !pend} style={{ opacity: c.status === 'dropped' || pend ? 0.55 : undefined }}>
                    <TableCell>
                      <Link component="button" variant="body2" aria-pressed={open === c.columnName} aria-label={`${c.columnName} 컬럼 이력 보기`}
                        onClick={() => setOpen(o => (o === c.columnName ? undefined : c.columnName))} style={{ fontFamily: "'Roboto Mono', monospace" }}>
                        {c.columnName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {editable ? (
                        <TextField
                          size="small" value={typeOf(c)} placeholder="타입" onChange={e => edit(c, { dataType: e.target.value })}
                          inputProps={{ 'aria-label': `${c.columnName} 타입` }} style={{ minWidth: 110 }}
                        />
                      ) : c.dataType ?? '—'}
                    </TableCell>
                    <TableCell>
                      {editable ? <Seg value={ynOf(c)} options={[['Y', 'Y'], ['N', 'N']]} onChange={v => edit(c, { collectYn: v })} label={`${c.columnName} 수집 여부`} /> : c.collectYn}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{state}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtAt(c.added.at)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{c.dropped ? fmtAt(c.dropped.at) : '—'}</TableCell>
                    {SIDES.map(s => <TableCell key={s}><VerdictPill row={bySide[s].get(c.columnName.toLowerCase())} side={s} fallback={fallback} /></TableCell>)}
                    <TableCell>
                      {c.status === 'active' && (
                        <Button size="small" variant="outlined" disabled={readOnly} aria-label={`${c.columnName} ${pend ? '삭제 취소' : '삭제'}`} onClick={() => onToggleDrop(c.columnName)}>
                          {pend ? '취소' : '삭제'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {[...extras.values()].map(e => {
                const pend = e.name in add;
                let state = e.sides[0] === 'source' ? '원천에만 있음' : '수집에만 있음';
                if (e.sides.length > 1) state = '레지스트리에 없음';
                if (pend) state = '추가 예정';
                return (
                  <TableRow key={`x-${e.name}`} selected={pend}>
                    <TableCell style={{ fontFamily: "'Roboto Mono', monospace" }}>{e.name}</TableCell>
                    <TableCell>{e.type ?? '—'}</TableCell>
                    <TableCell>{pend ? 'Y' : '—'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{state}</TableCell>
                    <TableCell colSpan={2} />
                    {SIDES.map(s => (
                      <TableCell key={s}>{e.sides.includes(s) ? <VerdictPill row={bySide[s].get(e.name.toLowerCase())} side={s} fallback={['—', 'neutral']} /> : <Pill tone="neutral">—</Pill>}</TableCell>
                    ))}
                    <TableCell>
                      <Button size="small" variant="outlined" disabled={readOnly} aria-label={`${e.name} ${pend ? '추가 취소' : '레지스트리에 추가'}`} onClick={() => onToggleAdd(e.name, e.type)}>
                        {pend ? '취소' : '추가'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {freeAdds.map(n => (
                <TableRow key={`a-${n}`} selected>
                  <TableCell style={{ fontFamily: "'Roboto Mono', monospace" }}>{n}</TableCell>
                  <TableCell>{add[n] ?? '—'}</TableCell>
                  <TableCell>Y</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>추가 예정</TableCell>
                  <TableCell colSpan={2} />
                  {SIDES.map(s => <TableCell key={s}><Pill tone="neutral">—</Pill></TableCell>)}
                  <TableCell>
                    <Button size="small" variant="outlined" disabled={readOnly} aria-label={`${n} 추가 취소`} onClick={() => onToggleAdd(n, add[n])}>취소</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!readOnly && (
                <TableRow>
                  <TableCell>
                    <TextField
                      size="small" value={newName} placeholder="새 컬럼 이름" onChange={e => setNewName(e.target.value.trim())}
                      error={!!newNameError} helperText={newNameError || undefined} inputProps={{ 'aria-label': '새 컬럼 이름', style: { fontFamily: "'Roboto Mono', monospace" } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={newType} placeholder="타입 (선택)" onChange={e => setNewType(e.target.value)} inputProps={{ 'aria-label': '새 컬럼 타입' }} style={{ minWidth: 110 }} />
                  </TableCell>
                  <TableCell colSpan={6} />
                  <TableCell>
                    <Button
                      size="small" variant="outlined" disabled={!newName || !!newNameError}
                      onClick={() => {
                        onToggleAdd(newName, newType.trim() || null);
                        setNewName('');
                        setNewType('');
                      }}
                    >
                      추가
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        {/* 컬럼 이력: 삭제 컬럼의 재추가·이전 incarnation 도 서버가 같은 이름으로 묶어 준다 */}
        {open && <ColumnHistoryPanel key={open} tableId={table.id} columnName={open} onClose={() => setOpen(undefined)} />}
        <Typography variant="caption" color="textSecondary">
          컬럼 이름을 누르면 그 컬럼의 추가·변경·삭제 이력이 나옵니다. 타입·수집 여부는 표에서 바로 고치고, 마지막 줄에서 새 컬럼을 직접 추가할 수 있습니다. 삭제는 상태만 바꾸고 시각·실행자·티켓을 이력에 남깁니다. 컬럼 변경은 위 편집의 티켓·사유와 함께 저장되며, 티켓이 필수입니다.
        </Typography>
      </>
    );
  };

  const activeCount = cols?.filter(c => c.status === 'active').length;
  return (
    <Paper variant="outlined" component="section" aria-label="컬럼">
      <Box p={2.5} display="flex" flexDirection="column" style={{ gap: 10 }}>
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
          <Typography variant="subtitle1" style={{ flex: 1 }}>
            컬럼{' '}
            {activeCount !== undefined && (
              <Typography component="span" variant="caption" color="textSecondary">
                활성 {activeCount}개
                {gap && table.collectYn === 'Y' && ` · 원천 GAP ${gap.source?.count ?? 0} · 수집 GAP ${gap.lake?.count ?? 0}`}
              </Typography>
            )}
          </Typography>
          <FormControlLabel control={<Checkbox size="small" checked={showDropped} onChange={e => setShowDropped(e.target.checked)} />} label="삭제된 컬럼 보기" />
        </Box>
        {body()}
      </Box>
    </Paper>
  );
};
